import { ArrowRight, Check, FileArchive, FolderOpen, ImageSquare, LinkSimple, LockKey, UploadSimple, WarningCircle } from "@phosphor-icons/react";
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../lib/auth";
import { fetchQuota, removeBackground, type Quota } from "../lib/api";
import { track } from "../lib/analytics";
import { exportFilename, presetById, renderExportBlob, type ExportPresetId } from "../lib/export";
import { downloadBlob, validateClientImage } from "../lib/image";
import { Turnstile } from "./Turnstile";

const Editor = lazy(() => import("./Editor").then((module) => ({ default: module.Editor })));

type UploadIntent = "transparent" | "white" | "marketplace";
type ItemStatus = "queued" | "processing" | "done" | "needs-review" | "error";

interface Selection {
  id: string;
  file: File;
  originalUrl: string;
  resultUrl?: string;
  resultBlob?: Blob;
  exportedBlob?: Blob;
  exportedName?: string;
  status: ItemStatus;
  error?: string;
}

const INTENTS: Array<{ id: UploadIntent; label: string; preset: ExportPresetId }> = [
  { id: "transparent", label: "Transparent PNG", preset: "transparent" },
  { id: "white", label: "White background", preset: "white" },
  { id: "marketplace", label: "Amazon / Etsy / Shopify", preset: "amazon" },
];

function batchLimit(quota: Quota | null): number {
  if (!quota || quota.plan === "anonymous") return 1;
  return quota.plan === "free" ? 10 : 50;
}

export function Uploader({ compact = false, onAuth }: { compact?: boolean; onAuth(): void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { user, token } = useAuth();
  const [items, setItems] = useState<Selection[]>([]);
  const [intent, setIntent] = useState<UploadIntent>("transparent");
  const [status, setStatus] = useState<"idle" | "ready" | "processing" | "done" | "error">("idle");
  const [phase, setPhase] = useState("Loading the removal model...");
  const [error, setError] = useState("");
  const [quota, setQuota] = useState<Quota | null>(null);
  const [dragging, setDragging] = useState(false);
  const [urlOpen, setUrlOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileReset, setTurnstileReset] = useState(0);

  const selectedPreset = INTENTS.find((option) => option.id === intent)?.preset ?? "transparent";
  const isBatch = items.length > 1;

  const refreshQuota = useCallback(async () => {
    try { setQuota(await fetchQuota(await token())); } catch { /* static preview and local frontend remain usable */ }
  }, [token]);

  useEffect(() => { void refreshQuota(); }, [refreshQuota, user]);
  useEffect(() => {
    function paste(event: ClipboardEvent) {
      const file = Array.from(event.clipboardData?.files ?? []).find((item) => item.type.startsWith("image/"));
      if (file) void chooseFiles([file], "clipboard");
    }
    window.addEventListener("paste", paste);
    return () => window.removeEventListener("paste", paste);
  }, [quota?.plan, user?.emailVerified]);

  async function chooseFiles(files: File[], source: "picker" | "folder" | "drop" | "clipboard" | "url" = "picker") {
    setError("");
    const limit = batchLimit(quota);
    const candidates = files.slice(0, limit);
    if (files.length > limit) setError(quota?.plan === "anonymous" || !quota ? "Sign in to queue multiple images. Anonymous use is limited to one." : `This plan can queue up to ${limit} images at once.`);
    const accepted: Selection[] = [];
    for (const file of candidates) {
      try {
        await validateClientImage(file, quota?.plan ?? (user?.emailVerified ? "free" : "anonymous"));
        accepted.push({ id: crypto.randomUUID(), file, originalUrl: URL.createObjectURL(file), status: "queued" });
      } catch (reason) {
        setError(reason instanceof Error ? reason.message : "One of these images is not supported.");
      }
    }
    if (!accepted.length) { setStatus("error"); return; }
    items.forEach((item) => { URL.revokeObjectURL(item.originalUrl); if (item.resultUrl) URL.revokeObjectURL(item.resultUrl); });
    setItems(accepted);
    setStatus("ready");
    track("upload_selected", { source, count: accepted.length, batch: accepted.length > 1, intent, format: accepted[0].file.type.replace("image/", "") || "unknown" });
  }

  async function chooseUrl() {
    setError("");
    try {
      const parsed = new URL(imageUrl);
      if (parsed.protocol !== "https:" && parsed.hostname !== "localhost") throw new Error("Use a direct HTTPS image URL.");
      const response = await fetch(parsed.toString(), { credentials: "omit", referrerPolicy: "no-referrer" });
      if (!response.ok) throw new Error("That image URL could not be downloaded.");
      const blob = await response.blob();
      const contentType = blob.type.split(";")[0];
      const extension = contentType === "image/jpeg" ? "jpg" : contentType.replace("image/", "") || "image";
      await chooseFiles([new File([blob], `remote-image.${extension}`, { type: contentType })], "url");
      setImageUrl("");
      setUrlOpen(false);
    } catch (reason) {
      setError(reason instanceof Error ? `${reason.message} The image host must allow browser downloads.` : "That URL could not be loaded.");
      setStatus("error");
    }
  }

  function checkReady(): boolean {
    if (import.meta.env.VITE_TURNSTILE_SITE_KEY && quota?.plan === "anonymous" && !turnstileToken) {
      setError("Complete the quick security check before removing the background.");
      setStatus("error");
      return false;
    }
    if (quota && quota.remaining !== null && quota.remaining < 1) {
      setError(quota.plan === "anonymous" ? "Your free removal is used. Sign in for three each day." : "Your daily removals are used. Try again after the UTC reset.");
      setStatus("error");
      return false;
    }
    return true;
  }

  async function processSingle() {
    const selection = items[0];
    if (!selection || !checkReady()) return;
    setStatus("processing");
    setPhase("Loading the removal model...");
    const phaseTimer = window.setTimeout(() => setPhase("Cutting around the subject..."), 650);
    track("removal_started", { account_type: quota?.plan ?? "unknown", intent, batch: false });
    setError("");
    try {
      const result = await removeBackground(selection.file, await token(), turnstileToken);
      const resultUrl = URL.createObjectURL(result.blob);
      setItems([{ ...selection, resultUrl, resultBlob: result.blob, status: "done" }]);
      updateQuota(result.quota);
      setStatus("done");
      track("removal_succeeded", { account_type: result.quota.plan, intent, batch: false });
    } catch (reason) {
      setError(!navigator.onLine ? "You appear to be offline. Reconnect and try again." : reason instanceof Error ? reason.message : "We could not process this image.");
      setStatus("error");
      track("removal_failed", { account_type: quota?.plan ?? "unknown", batch: false, reason: reason instanceof Error ? reason.name : "unknown" });
    } finally {
      window.clearTimeout(phaseTimer);
      setTurnstileToken("");
      setTurnstileReset((value) => value + 1);
    }
  }

  function updateQuota(next: Quota) {
    setQuota(next);
    window.dispatchEvent(new CustomEvent("kleanbg:quota-updated", { detail: next }));
  }

  function reset() {
    items.forEach((item) => { URL.revokeObjectURL(item.originalUrl); if (item.resultUrl) URL.revokeObjectURL(item.resultUrl); });
    setItems([]);
    setStatus("idle");
    setError("");
  }

  if (status === "done" && items.length === 1 && items[0].resultUrl && items[0].resultBlob) {
    return <Suspense fallback={<div className="editor-loading" aria-live="polite"><span /><span /><span /><p>Opening the editor...</p></div>}><Editor originalUrl={items[0].originalUrl} resultUrl={items[0].resultUrl} sourceName={items[0].file.name} initialPreset={selectedPreset} onStartOver={reset} /></Suspense>;
  }

  return (
    <section className={compact ? "uploader compact" : "uploader"} id="upload" aria-label="Background remover">
      <div className="quota-row">
        <span>{quota ? quota.limit === null ? "Unlimited removals" : `${quota.remaining} of ${quota.limit} removal${quota.limit === 1 ? "" : "s"} left` : "Private image processing"}</span>
        {quota?.plan === "anonymous" && <button onClick={onAuth}>Sign in for 3 daily <ArrowRight size={15} /></button>}
      </div>
      {!items.length ? (
        <>
          <div className="intent-picker" role="radiogroup" aria-label="Finished image type">
            {INTENTS.map((option) => <button key={option.id} role="radio" aria-checked={intent === option.id} className={intent === option.id ? "active" : ""} onClick={() => { setIntent(option.id); track("upload_intent_selected", { intent: option.id }); }}>{option.label}</button>)}
          </div>
          <p className="anonymous-promise">No account required for a single image.</p>
          <div
            className={dragging ? "drop-zone dragging" : "drop-zone"}
            onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              const files = Array.from(event.dataTransfer.files).filter((file) => file.type.startsWith("image/"));
              if (files.length) void chooseFiles(files, "drop");
              else {
                const url = event.dataTransfer.getData("text/uri-list") || event.dataTransfer.getData("text/plain");
                if (url) { setImageUrl(url.trim()); setUrlOpen(true); }
              }
            }}
          >
            <div className="upload-icon"><UploadSimple size={28} weight="bold" /></div>
            <h2>Drop images here</h2>
            <p>One image, several images, a folder, or paste from your clipboard</p>
            <div className="drop-actions">
              <button className="primary-button" onClick={() => inputRef.current?.click()}>Choose images</button>
              <label className="secondary-button"><FolderOpen size={18} /> Choose folder<input hidden type="file" accept="image/png,image/jpeg,image/webp" multiple ref={(node) => node?.setAttribute("webkitdirectory", "")} onChange={(event) => void chooseFiles(Array.from(event.target.files ?? []), "folder")} /></label>
            </div>
            <button className="url-toggle" onClick={() => setUrlOpen((value) => !value)}><LinkSimple size={17} /> Add image URL</button>
            {urlOpen && <div className="url-row"><label><span className="sr-only">Direct image URL</span><input type="url" placeholder="https://example.com/product.jpg" value={imageUrl} onChange={(event) => setImageUrl(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void chooseUrl(); }} /></label><button onClick={() => void chooseUrl()}>Add</button></div>}
            <input ref={inputRef} hidden type="file" accept="image/png,image/jpeg,image/webp" multiple onChange={(event) => void chooseFiles(Array.from(event.target.files ?? []), "picker")} />
            <small>PNG, JPEG or WebP. {quota?.plan === "anonymous" || !quota ? "Up to 5 MB and 25 MP." : quota.plan === "free" ? "Up to 20 MB and 50 MP each." : "Up to 20 MB and 100 MP each."}</small>
          </div>
        </>
      ) : isBatch ? (
        <BatchWorkspace items={items} setItems={setItems} quota={quota} updateQuota={updateQuota} token={token} initialPresetId={selectedPreset} onReset={reset} />
      ) : (
        <div className="selection-preview">
          <img src={items[0].originalUrl} alt="Selected original" />
          <div className="selection-copy">
            <span className="file-ok"><Check size={17} weight="bold" /> Ready</span>
            <h2>{items[0].file.name}</h2>
            <p>{(items[0].file.size / 1024 / 1024).toFixed(1)} MB. Finished as {presetById(selectedPreset).description.toLowerCase()}.</p>
            {status === "processing" ? (
              <div className="processing-state" aria-live="polite"><span /><p>{phase}</p><small>First image can take longer. Next ones are usually faster.</small></div>
            ) : (
              <div className="selection-actions">
                {quota?.plan === "anonymous" || !quota ? <Turnstile onToken={setTurnstileToken} resetKey={turnstileReset} /> : null}
                <button className="primary-button" onClick={() => void processSingle()}>Create finished image <ArrowRight size={19} weight="bold" /></button>
                <button className="secondary-button" onClick={reset}>Choose another</button>
              </div>
            )}
          </div>
        </div>
      )}
      {error && <div className="inline-error" role="alert"><WarningCircle size={20} weight="fill" /><span>{error}</span>{quota?.plan === "anonymous" && quota.remaining === 0 && <button onClick={onAuth}>Sign in</button>}</div>}
      {!compact && <div className="privacy-line"><LockKey size={17} weight="bold" /><span>Uploaded for processing. Never stored or used for training.</span><ImageSquare size={17} /><span>EXIF and GPS removed on export.</span></div>}
    </section>
  );
}

function BatchWorkspace({ items, setItems, quota, updateQuota, token, initialPresetId, onReset }: {
  items: Selection[];
  setItems: React.Dispatch<React.SetStateAction<Selection[]>>;
  quota: Quota | null;
  updateQuota(next: Quota): void;
  token(): Promise<string | null>;
  initialPresetId: ExportPresetId;
  onReset(): void;
}) {
  const [working, setWorking] = useState(false);
  const [batchError, setBatchError] = useState("");
  const [presetId, setPresetId] = useState(initialPresetId);
  const preset = presetById(presetId);
  const finished = items.filter((item) => item.status === "done" || item.status === "needs-review");
  const progress = items.filter((item) => item.status !== "queued" && item.status !== "processing").length;

  async function processQueue() {
    setWorking(true);
    setBatchError("");
    track("batch_started", { count: items.length, preset: preset.id, account_type: quota?.plan ?? "unknown" });
    let available = quota?.remaining;
    for (const item of items) {
      if (available !== null && available !== undefined && available < 1) {
        setItems((current) => current.map((candidate) => candidate.id === item.id ? { ...candidate, status: "error", error: "Daily limit reached" } : candidate));
        continue;
      }
      setItems((current) => current.map((candidate) => candidate.id === item.id ? { ...candidate, status: "processing", error: undefined } : candidate));
      try {
        const result = await removeBackground(item.file, await token());
        const exportedBlob = await renderExportBlob(result.blob, preset.settings);
        const resultUrl = URL.createObjectURL(exportedBlob);
        const exportedName = exportFilename(item.file.name, preset.settings);
        setItems((current) => current.map((candidate) => candidate.id === item.id ? { ...candidate, status: "done", resultBlob: result.blob, resultUrl, exportedBlob, exportedName } : candidate));
        available = result.quota.remaining;
        updateQuota(result.quota);
      } catch (reason) {
        setItems((current) => current.map((candidate) => candidate.id === item.id ? { ...candidate, status: "error", error: reason instanceof Error ? reason.message : "Processing failed" } : candidate));
      }
    }
    setWorking(false);
    track("batch_completed", { count: items.length, preset: preset.id });
  }

  async function downloadAll() {
    const files = finished.flatMap((item) => item.exportedBlob && item.exportedName ? [{ name: item.exportedName, blob: item.exportedBlob }] : []);
    if (!files.length) return;
    try {
      const { createZip } = await import("../lib/zip");
      downloadBlob(await createZip(files), `kleanbg-${preset.id}-${files.length}-images.zip`);
      track("batch_zip_downloaded", { count: files.length, preset: preset.id });
    } catch {
      setBatchError("The ZIP could not be created in this browser. Download the images individually instead.");
    }
  }

  return <div className="batch-workspace">
    <div className="batch-header">
      <div><span>{items.length} images queued</span><h2>One preset. One ZIP.</h2><p>Each successful image still counts toward your daily removal allowance.</p></div>
      <label>Export preset<select value={presetId} onChange={(event) => { const next = event.target.value as ExportPresetId; setPresetId(next); track("export_preset_selected", { preset: next, batch: true }); }} disabled={working || finished.length > 0}>
        {(["transparent", "white", "amazon", "etsy", "shopify", "ebay"] as ExportPresetId[]).map((id) => { const option = presetById(id); return <option key={id} value={id}>{option.label}: {option.description}</option>; })}
      </select></label>
    </div>
    {working && <div className="batch-progress" aria-live="polite"><span style={{ width: `${Math.max(6, (progress / items.length) * 100)}%` }} /><p>{progress} of {items.length} finished. Cutting images one at a time.</p></div>}
    <div className="batch-list">
      {items.map((item) => <article key={item.id} className={`batch-item ${item.status}`}>
        <img src={item.resultUrl ?? item.originalUrl} alt="" />
        <div><strong>{item.file.name}</strong><span>{item.status === "needs-review" ? "Needs review" : item.status === "processing" ? "Cutting..." : item.status === "done" ? item.exportedName : item.status === "error" ? item.error : "Queued"}</span></div>
        {(item.status === "done" || item.status === "needs-review") && <div className="batch-item-actions"><button onClick={() => setItems((current) => current.map((candidate) => candidate.id === item.id ? { ...candidate, status: candidate.status === "needs-review" ? "done" : "needs-review" } : candidate))}>{item.status === "needs-review" ? "Mark ready" : "Needs review"}</button>{item.exportedBlob && item.exportedName ? <button onClick={() => downloadBlob(item.exportedBlob!, item.exportedName!)}>Download</button> : null}</div>}
      </article>)}
    </div>
    {batchError && <p className="batch-error" role="alert">{batchError}</p>}
    <div className="batch-actions">
      {!finished.length ? <button className="primary-button" disabled={working} onClick={() => void processQueue()}>{working ? "Processing queue" : "Process queue"}</button> : <button className="primary-button" disabled={working} onClick={() => void downloadAll()}><FileArchive size={20} weight="bold" /> Download all as ZIP</button>}
      <button className="secondary-button" disabled={working} onClick={onReset}>Start over</button>
    </div>
  </div>;
}
